import { AppRouteEnum, NavigationSourceQueryEnum, QuoteFlowStepEnum } from '@lib/enums';
import { ApiRoutes } from '@lib/enums/api/ApiRoutes';

import type { TWorkflowSchema } from '@lib/types/TWorkflowSchema';
import type { TQuote } from '@models';

export const quoteWorkflowSchema = {
  [QuoteFlowStepEnum.VehicleDetails]: {
    slug: AppRouteEnum.QuoteFlowVehicleDetails,
    header: 'Vehicle Details',
    buildSubheader: () => 'Re-check vehicle details we have',
    label: 'Vehicle Details',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowVehicleDetails,
  },
  [QuoteFlowStepEnum.UsageAndModifications]: {
    slug: AppRouteEnum.QuoteFlowVehicleModifications,
    header: 'Usage & Modifications',
    buildSubheader: () => 'Fill in the details of vehicle modifications',
    label: 'Modifications',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowVehicleModifications,
  },
  [QuoteFlowStepEnum.SelectCoverage]: {
    slug: AppRouteEnum.QuoteFlowSelectPlan,
    header: 'Select Coverage',
    label: 'Select Coverage',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowSelectPlan,
  },
  [QuoteFlowStepEnum.PaymentMethod]: {
    slug: AppRouteEnum.QuoteFlowPaymentMethod,
    header: 'Payment Method',
    buildSubheader: () => 'Add payment method details',
    label: 'Payment Method',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowPaymentMethod,
  },
  [QuoteFlowStepEnum.ContactInfo]: {
    slug: AppRouteEnum.QuoteFlowContactInfo,
    header: 'Collect Missing Contact Info',
    label: 'Contact Info',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowContactInfo,
  },
  [QuoteFlowStepEnum.SecondHolder]: {
    slug: AppRouteEnum.QuoteFlowSecondHolder,
    header: 'Add Additional Policy Holder',
    buildSubheader: () => 'You can add your relatives’ names so they can use the policy',
    label: '2nd Holder',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowAdditionalHolders,
  },
  [QuoteFlowStepEnum.Payment]: {
    slug: AppRouteEnum.QuoteFlowPayments,
    header: 'Payment Details',
    buildSubheader: () => 'Add payment method details',
    label: 'Payment Details',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowPaymentDetails,
  },
  [QuoteFlowStepEnum.Closed]: {
    slug: AppRouteEnum.QuoteFlowClosed,
    header: 'Closed Quote',
    buildSubheader: () =>
      'No further changes can be made to this quote as it is closed. Please start a new quote or continue an existing quote below.',
    label: 'Closed',
  },
  [QuoteFlowStepEnum.QuoteWrapUp]: {
    slug: AppRouteEnum.QuoteFlowWrapUp,
    header: 'Wrap Up',
    label: 'Wrap Up',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowWrapCall,
  },
  [QuoteFlowStepEnum.CoverageAvailability]: {
    header: '',
    slug: AppRouteEnum.QuoteFlowCoverageAvailability,
    label: 'Coverage Availability',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowWrapCall,
  },
  [QuoteFlowStepEnum.VinAndMileage]: {
    slug: AppRouteEnum.QuoteFlowMissingVehicleDetails,
    header: 'Missing Vehicle Details',
    buildSubheader: (quote?: TQuote) =>
      quote?.isExactMileageRequired === true
        ? "We can't sell the plan without exact mileage and VIN"
        : "We can't sell the plan without VIN",
    label: 'Vin & Mileage',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowVinAndMileage,
  },
  [QuoteFlowStepEnum.Finalize]: {
    slug: AppRouteEnum.QuoteFlowFinalize,
    header: 'Finalize',
    label: 'Finalize',
  },
  [QuoteFlowStepEnum.SaleComplete]: {
    slug: AppRouteEnum.QuoteFlowSaleComplete,
    header: 'Sale Complete',
    label: 'Sale Complete',
    buildSubheader: () => 'No further changes can be made to this quote as it is sold.',
  },
  [QuoteFlowStepEnum.SendQuote]: {
    slug: AppRouteEnum.SendQuote,
    header: 'Send Quote',
    label: 'Send Quote',
  },
  [QuoteFlowStepEnum.TransferCall]: {
    slug: AppRouteEnum.QuoteFlowTransferCall,
    queryParams: { source: NavigationSourceQueryEnum.WrapCall },
    header: 'Transfer Call',
    buildSubheader: () => 'Hand the call off to another representative',
    label: 'Transfer Call',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowWrapCall,
  },
  [QuoteFlowStepEnum.Recap]: {
    slug: AppRouteEnum.QuoteFlowRecap,
    header: 'Recap',
    label: 'Recap',
    buildSubheader: () => 'Review the following checklist with the customer.',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowCompleteRecap,
  },
  [QuoteFlowStepEnum.Equity]: {
    slug: AppRouteEnum.QuoteFlowEquityCalculation,
    header: 'Equity Calculation',
    label: 'Equity',
    buildSubheader: () =>
      'To determine how much money is carried forward to the down payment of the rewritten policy.',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowEquity,
  },
  [QuoteFlowStepEnum.ConfirmRewrite]: {
    slug: AppRouteEnum.QuoteFlowConfirmRewrite,
    header: 'Confirm Rewrite',
    label: 'Confirm Rewrite',
    buildSubheader: () => 'Review the following policy changes with the customer.',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowConfirmRewrite,
  },
  [QuoteFlowStepEnum.CompleteRewrite]: {
    slug: AppRouteEnum.QuoteFlowCompleteRewrite,
    header: 'Complete Rewrite',
    label: 'Complete Rewrite',
    apiEndpoint: ApiRoutes.Quotes.QuoteFlowCompleteRewrite,
  },
} satisfies TWorkflowSchema<TQuote, QuoteFlowStepEnum>;
